<template>
  <v-dialog :value="value" max-width="720" scrollable @input="$emit('input', $event)">
    <v-card v-if="row" class="kf-match">
      <v-card-title class="kf-match__head">
        <div>
          <div class="kf-match__title">Match {{ row.reference }} to an offer</div>
          <div class="kf-match__sub">
            {{ row.product || "-" }} · {{ fmt.mobile(row.mobile) }}
            <template v-if="row.status"> · lender says {{ row.status }}</template>
            <template v-if="row.disbursal_amount"> · {{ fmt.currency(row.disbursal_amount) }}</template>
          </div>
        </div>
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)">
          <LucideIcon name="x" :size="20" />
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary" />
        <template v-else>
          <div v-if="!candidates.length" class="kf-muted kf-match__gap">
            No account with this mobile has an offer yet. Enter an offer ID below to
            match it to another account's offer.
          </div>
          <v-radio-group v-else v-model="selected" class="mt-0" hide-details>
            <v-radio v-for="o in candidates" :key="o.offer_id" :value="o.offer_id">
              <template #label>
                <span>
                  Offer {{ o.offer_id }} · {{ o.product }} · {{ o.status }}
                  <span class="kf-muted">
                    · {{ fmt.date(o.created) }}
                    <template v-if="o.flag_disbursed"> · disbursed {{ fmt.currency(o.disbursal_amount) }}</template>
                    <template v-if="!o.is_offer_available"> · not available</template>
                    <template v-if="o.same_product"> · same product</template>
                  </span>
                </span>
              </template>
            </v-radio>
          </v-radio-group>

          <v-text-field
            v-model.trim="typedOfferId"
            class="mt-4"
            label="Or another offer ID"
            dense
            outlined
            hide-details
            @input="selected = null"
          />
          <v-checkbox
            v-if="otherAccount"
            v-model="force"
            class="mt-2"
            hide-details
            label="This offer belongs to another account -- match it anyway"
          />
          <v-textarea
            v-model.trim="note"
            class="mt-4"
            :label="force ? 'Note (required: why this account)' : 'Note (optional)'"
            rows="2"
            auto-grow
            dense
            outlined
            hide-details
          />
          <div class="kf-muted kf-match__gap">
            Matching writes the lender's newest status
            <template v-if="row.status">({{ row.status }}<template v-if="row.disbursal_amount">, {{ fmt.currency(row.disbursal_amount) }}</template>)</template>
            onto the offer, unless the offer already has a newer one. It cannot be undone from here.
          </div>
          <div v-if="error" class="kf-match__error">{{ error }}</div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Cancel</v-btn>
        <v-btn color="primary" depressed :disabled="!canSubmit" :loading="saving" @click="submit">
          Match to offer {{ offerId || "" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import fmt from "@/app-js/karrofin/format";
import api from "@/app-js/karrofin/api";

/**
 * Ops tie a lender-reported lead (MIS-<id>) to an offer. The candidates are the
 * offers of the account with the lead's mobile; any other offer ID can be typed,
 * and then the server wants `force` plus a note. Emits `matched` on success.
 */
export default {
  name: "KfReportLeadMatchDialog",
  props: {
    value: { type: Boolean, default: false },
    row: { type: Object, default: null },
  },
  data() {
    return {
      fmt,
      loading: false,
      saving: false,
      error: "",
      candidates: [],
      selected: null,
      typedOfferId: "",
      force: false,
      note: "",
    };
  },
  computed: {
    offerId() {
      if (this.selected) return this.selected;
      const typed = parseInt(this.typedOfferId, 10);
      return Number.isFinite(typed) && typed > 0 ? typed : null;
    },
    otherAccount() {
      return !!this.offerId && !this.candidates.some((o) => o.offer_id === this.offerId);
    },
    canSubmit() {
      if (!this.offerId || this.saving) return false;
      return !this.otherAccount || (this.force && !!this.note);
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
  },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      this.candidates = [];
      this.selected = null;
      this.typedOfferId = "";
      this.force = false;
      this.note = "";
      api
        .getReportLeadCandidates(this.row.report_lead_id)
        .then((result) => {
          this.candidates = result.offers || [];
          const same = this.candidates.filter((o) => o.same_product);
          if (same.length === 1) this.selected = same[0].offer_id;
        })
        .catch((err) => {
          this.error = this.message(err, "Could not load this borrower's offers");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submit() {
      this.saving = true;
      this.error = "";
      api
        .matchReportLead(this.row.report_lead_id, {
          offer_id: this.offerId,
          note: this.note,
          force: this.otherAccount && this.force,
        })
        .then((result) => {
          this.$emit("matched", result);
          this.$emit("input", false);
        })
        .catch((err) => {
          this.error = this.message(err, "Could not match this lead");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    message(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && data.detail) || fallback;
    },
  },
};
</script>

<style scoped>
.kf-match__head {
  align-items: flex-start;
  flex-wrap: nowrap;
}
.kf-match__title {
  font-size: var(--kf-fs-body);
  font-weight: 700;
  color: #000;
}
.kf-match__sub,
.kf-muted {
  font-size: var(--kf-fs-label);
  color: #54636f;
  font-weight: 400;
}
.kf-match__gap {
  margin-top: 12px;
}
.kf-match__error {
  margin-top: 10px;
  color: #b42318;
  font-size: var(--kf-fs-hint);
}
</style>
